import { FC, useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useTranslate } from 'hooks/useTranslate';
import { es, enUS } from 'date-fns/locale';

import Page from 'modules/_shared/components/Pages/Page';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import Breadcrumb, {
  BreadcrumbItem,
} from 'modules/_shared/components/Breadcrumb';
import Button from 'modules/_shared/components/Buttons';
import GoBackButton from 'modules/_shared/components/Buttons/GoBackButton';

import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import menuTypes from 'constants/menuTypes';

import {
  getGlobalNotificationsBySociety,
  updateGlobalNotification,
} from 'modules/_shared/redux/globalNotificationActions';
import { State } from 'redux/initialState';
import { Notification } from 'types/Notification';

import { EQUITY_MARKET_NOTIFICATION_CATEGORY } from 'modules/incentivesMarket/constants/incentivesMarketNotifications';
import { useIncentivesMarket } from '../../contexts/IncentivesMarketContext';
import IncentivesMarketNotificationsList from './components/IncentivesMarketNotificationsList';

import '../../styles/IncentivesMarket.scss';

export enum Tabs {
  RECENT = 'RECENT', // eslint-disable-line no-unused-vars
  CLOSED = 'CLOSED', // eslint-disable-line no-unused-vars
}

export enum TabStatus {
  UNREAD = 'UNREAD', // eslint-disable-line no-unused-vars
  READ = 'READ', // eslint-disable-line no-unused-vars
}

const IncentivesMarketNotifications: FC = () => {
  const { t, i18n } = useTranslate();
  const dispatch = useDispatch();
  const history = useHistory();
  const { data } = useIncentivesMarket();
  const user = useSelector((state: State) => state.user);

  const globalNotifications = useSelector(
    (state: State) => state.globalNotifications as Notification[]
  );

  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.RECENT);
  const [isLoading, setIsLoading] = useState(true);

  useGetMenu(menuTypes.NULL);

  const dateLocale = i18n.language?.startsWith('en') ? enUS : es;

  const incentivesMarketNotifications = useMemo(() => {
    const uid = user?._id ? String(user._id) : '';
    const sid = data.societyId ? String(data.societyId) : '';
    if (!uid || !sid) return [];

    return (globalNotifications || []).filter((n) => {
      const sameUser = n.userId && String(n.userId) === uid;
      const sameSociety = n.societyId && String(n.societyId) === sid;
      const sameCategory = n.category === EQUITY_MARKET_NOTIFICATION_CATEGORY;
      return sameUser && sameSociety && sameCategory;
    });
  }, [globalNotifications, user?._id, data.societyId]);

  const recentList = useMemo(
    () =>
      [...incentivesMarketNotifications]
        .filter((n) => n.status === TabStatus.UNREAD)
        .sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        ),
    [incentivesMarketNotifications]
  );

  const closedList = useMemo(
    () =>
      [...incentivesMarketNotifications]
        .filter((n) => n.status === TabStatus.READ)
        .sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        ),
    [incentivesMarketNotifications]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!data.societyId) return;
      setIsLoading(true);
      try {
        await dispatch(
          getGlobalNotificationsBySociety(data.societyId) as never
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [dispatch, data.societyId]);

  const handleMarkAsRead = useCallback(
    async (notificationId: string) => {
      await dispatch(
        updateGlobalNotification({
          _id: notificationId,
          status: TabStatus.READ,
        }) as never
      );
    },
    [dispatch]
  );

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('Dashboard'), to: '/' },
      { label: data.societyName, to: `/sociedad/${data.societyId}` },
      {
        label: t('IncentivesMarket'),
        to: `/mercado-de-incentivos/${data.societyId}`,
      },
      { label: t('IncentivesMarketNotificationsBreadcrumb'), isActive: true },
    ],
    [t, data.societyName, data.societyId]
  );

  return (
    <Page id="incentives-market-notifications-page">
      <div className="nk-block-head-content mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="nk-block-head page-header-subtitle incentives-market-notif-head">
        <div className="nk-block-between-md">
          <div className="nk-block-head-content">
            <div className="d-flex align-items-center">
              <h3 className="nk-block-title page-title">
                {t('IncentivesMarketNotificationsPageTitle')}
              </h3>
            </div>

            <div className="nk-block-des">
              <p>{t('IncentivesMarketNotificationsPageDescription')}</p>
            </div>
          </div>

          <div className="nk-block-head-content">
            <ul className="nk-block-tools gx-3">
              <li>
                <Button
                  className="btn-white"
                  variant="light"
                  dimStyle
                  icon="arrow-long-right"
                  text={t('IncentivesMarketNotificationsGoToMarket')}
                  onClick={() =>
                    history.push(`/mercado-de-incentivos/${data.societyId}`)
                  }
                />
              </li>
              <li>
                <GoBackButton icon="ni-arrow-left" />
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="nk-block nk-block-lg">
        <div className="card card-bordered incentives-market-notif-card">
          <ul
            className="nav nav-tabs nav-tabs-notif card-inner pt-0 pb-0"
            role="tablist"
          >
            <li className="nav-item" role="presentation">
              <Button
                className={`nav-link ${
                  activeTab === Tabs.RECENT ? 'active' : ''
                }`}
                onClick={() => setActiveTab(Tabs.RECENT)}
                role="tab"
                aria-selected={activeTab === Tabs.RECENT}
              >
                <em className="icon ni ni-notify mr-1" />
                {t('IncentivesMarketNotificationsTabRecent')}
                <span className="badge badge-dim badge-primary ml-1">
                  {recentList.length}
                </span>
              </Button>
            </li>
            <li className="nav-item" role="presentation">
              <Button
                type="button"
                className={`nav-link ${
                  activeTab === Tabs.CLOSED ? 'active' : ''
                }`}
                onClick={() => setActiveTab(Tabs.CLOSED)}
                role="tab"
                aria-selected={activeTab === Tabs.CLOSED}
              >
                <em className="icon ni ni-archive mr-1" />
                {t('IncentivesMarketNotificationsTabClosed')}
                <span className="badge badge-dim badge-secondary ml-1">
                  {closedList.length}
                </span>
              </Button>
            </li>
          </ul>

          <div className="card-inner p-0">
            <div
              className={`panel-notif ${
                activeTab === Tabs.RECENT ? 'active' : ''
              }`}
              role="tabpanel"
              hidden={activeTab !== Tabs.RECENT}
            >
              <IncentivesMarketNotificationsList
                notifications={recentList}
                activeTab={Tabs.RECENT}
                locale={dateLocale}
                onMarkAsRead={handleMarkAsRead}
              />
            </div>
            <div
              className={`panel-notif ${
                activeTab === Tabs.CLOSED ? 'active' : ''
              }`}
              role="tabpanel"
              hidden={activeTab !== Tabs.CLOSED}
            >
              <IncentivesMarketNotificationsList
                notifications={closedList}
                activeTab={Tabs.CLOSED}
                locale={dateLocale}
                onMarkAsRead={handleMarkAsRead}
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading && <CustomLoading />}
    </Page>
  );
};

export default IncentivesMarketNotifications;
